'use client'

import { useState, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider, isServer } from '@tanstack/react-query'
import { Toaster } from '@/core/components/sonner'
import { AuthProvider } from '@/features/auth'
import type { ClientAuthUser } from '@/features/auth'
import { BackgroundUploaderProvider } from '@/shared/media'
import { queryClientDefaults } from '@/shared/state/query-cache-policy'
import { SessionZipProvider } from '@/shared/state/session-zip-provider'
import { ReadReceiptProvider } from '@/features/notifications/state/read-receipt-provider'
import {
  AppInviteProvider,
  AppInviteModal,
  AppInviteWelcomeBar,
  usePendingWelcome,
} from '@/shared/app-invite'
import { SocketProvider } from '@/features/realtime'

interface ProvidersProps {
  children: ReactNode
  user: ClientAuthUser | null
  /** Realtime server origin, resolved server-side from the active environment. */
  socketUrl: string
}

/**
 * Fires the welcome invite once on the post-auth destination page, consuming the
 * one-shot flag set during the login → redirect navigation. Rendered inside
 * AppInviteProvider so it can reach the shared invite state.
 */
function PendingWelcomeConsumer() {
  usePendingWelcome()
  return null
}

export function Providers({ children, user, socketUrl }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      // `isServer` decides the gcTime: a server-side gc timer would pin this
      // whole render for an hour — see queryClientDefaults.
      new QueryClient({ defaultOptions: { queries: queryClientDefaults(isServer) } })
  )

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider user={user}>
        <SocketProvider url={socketUrl}>
          <AppInviteProvider>
            <ReadReceiptProvider>
              <SessionZipProvider>
                <BackgroundUploaderProvider>{children}</BackgroundUploaderProvider>
              </SessionZipProvider>
            </ReadReceiptProvider>
            <PendingWelcomeConsumer />
            <AppInviteModal />
            <AppInviteWelcomeBar />
          </AppInviteProvider>
        </SocketProvider>
      </AuthProvider>
      <Toaster />
    </QueryClientProvider>
  )
}
